import { useFormikContext } from 'formik';
import { Stack, Typography } from '@mui/material';
import themes from '~/themes';
import { useAuth } from '~/providers/AuthProvider';
import { getTimeZoneNameAbbr } from '~/utils/formatter';
import { useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { map } from 'lodash';
import { KeyboardArrowRight } from '@mui/icons-material';
import CheckBox from '~/components/common/CheckBox';
import Button from '~/components/common/Button';
import EditIcon from '~/assets/images/icons/edit.svg';
import DisableEditIcon from '~/assets/images/icons/edit-gray.svg';
import CloseIcon from '~/assets/images/icons/close.svg';
import SelectIcon from '~/assets/images/icons/bulk-select.svg';
import { formatSimpleTime, transformTypeToTitle } from '~/utils/common';
import { IDriver } from '../../../types';
import DialogAddShiftSchedule from './DialogAddShiftSchedule';

const RegularSchedule = () => {
  const { account } = useAuth();
  const { values, setFieldValue } = useFormikContext<IDriver>();
  const [hasBulkSelect, setHasBulkSelect] = useState(false);
  const [daySelected, setDaySelected] = useState<Array<string>>([]);
  const [openAddShifts, setOpenAddShifts] = useState(false);

  const currentTimeZone = useMemo(() => {
    const { timezone } = account;
    if (!timezone) return '';
    if (timezone === 'auto') return getTimeZoneNameAbbr(moment.tz.guess());
    return getTimeZoneNameAbbr(timezone);
  }, [account]);

  const { delivery } = values;
  const { delivery_hours } = delivery;

  const handleCancelBulk = () => {
    setDaySelected([]);
    setHasBulkSelect(false);
  };

  const handleEditTimeSlot = (weekDay) => {
    if (!hasBulkSelect) {
      setDaySelected([weekDay]);
      setOpenAddShifts(true);
    }
  };

  const renderSlot = (weekDay) => {
    let content = null;
    const item = delivery_hours[weekDay];

    if (!item?.available) {
      content = <Typography variant='body1'>Off shift</Typography>;
    } else if (item.all_hour) {
      content = <Typography variant='body1'>24 hours</Typography>;
    } else {
      content = (
        <Typography
          variant='body1'
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item?.shifts
            ?.map((shift) => {
              const start = formatSimpleTime(moment(shift.start_time, 'HH:mm'));
              const end = formatSimpleTime(moment(shift.end_time, 'HH:mm'));
              return `${start} - ${end}`;
            })
            .join(', ')}
        </Typography>
      );
    }

    return (
      <Stack
        key={`${weekDay}`}
        flexDirection='row'
        alignItems='center'
        sx={{
          'padding': '0px 8px',
          '&:hover': {
            backgroundColor: '#E5E3F4',
            cursor: 'pointer',
          },
          'minHeight': 42,
        }}
        onClick={() => handleEditTimeSlot(weekDay)}
      >
        <Stack sx={{ width: 140 }}>
          {hasBulkSelect ? (
            <CheckBox
              checked={daySelected.includes(weekDay)}
              onChange={(e) => {
                const { checked } = e.target;
                if (checked) {
                  setDaySelected((prevState) => [...prevState, weekDay]);
                } else {
                  setDaySelected((prevState) =>
                    prevState?.filter((day) => day !== weekDay),
                  );
                }
              }}
              defaultCheckedIcon
              label={transformTypeToTitle(weekDay)}
            />
          ) : (
            <Typography variant='body1'>
              {transformTypeToTitle(weekDay)}
            </Typography>
          )}
        </Stack>
        <Stack
          flex={1}
          sx={{
            width: '100%',
            overflow: 'hidden',
          }}
        >
          {content}
        </Stack>
        <KeyboardArrowRight
          sx={{ color: `${themes.color.violet900} !important` }}
        />
      </Stack>
    );
  };

  return (
    <Stack flexDirection='column' gap={1.8}>
      <Stack flexDirection='column' gap={0.8}>
        <Typography fontWeight={600} color={themes.color.black}>
          Regular Schedule
        </Typography>
        <Typography color={themes.color.black}>
          Current location timezone: {currentTimeZone}
        </Typography>
      </Stack>
      <Stack flexDirection='column'>
        <Stack flexDirection='row' sx={{ padding: '6px 8px' }}>
          <Typography
            variant='caption'
            sx={{
              color: '#8C95BA',
              width: 140,
            }}
          >
            Day of the week
          </Typography>
          <Typography
            variant='caption'
            sx={{
              color: '#8C95BA',
            }}
          >
            Shift hours
          </Typography>
        </Stack>
        {map(delivery_hours, (_, key) => renderSlot(key))}
        <Stack flexDirection='row' gap={1.6}>
          {hasBulkSelect ? (
            <>
              <Button
                noRounder
                buttonType='default'
                sx={{
                  mt: 1.5,
                  gap: '4px',
                  width: 'fit-content',
                }}
                onClick={() => {
                  setOpenAddShifts(true);
                }}
                disabled={!daySelected?.length}
              >
                <img
                  src={daySelected?.length ? EditIcon : DisableEditIcon}
                  alt=''
                  style={{
                    width: 16,
                    height: 16,
                  }}
                />
                Bulk edit
              </Button>
              <Button
                noRounder
                buttonType='default'
                sx={{
                  mt: 1.5,
                  gap: '4px',
                  width: 'fit-content',
                }}
                onClick={handleCancelBulk}
              >
                <img src={CloseIcon} alt='' />
                Cancel
              </Button>
            </>
          ) : (
            <Button
              noRounder
              buttonType='default'
              sx={{
                mt: 1.5,
                gap: '4px',
                width: 'fit-content',
              }}
              onClick={() => setHasBulkSelect(true)}
            >
              <img src={SelectIcon} alt='' />
              Bulk Select
            </Button>
          )}
        </Stack>
      </Stack>

      {openAddShifts && (
        <DialogAddShiftSchedule
          open
          onClose={() => {
            if (!hasBulkSelect) {
              setDaySelected([]);
            }
            setOpenAddShifts(false);
          }}
          data={daySelected?.map((weekDay) => ({
            weekDay,
            ...delivery_hours[weekDay],
          }))}
          onSave={(val) => {
            const { weekDay, ...rest } = val;
            const updatedDeliveryHours = { ...values.delivery.delivery_hours };
            daySelected.forEach((wd) => {
              updatedDeliveryHours[wd] = rest;
            });
            setFieldValue('delivery', {
              ...values.delivery,
              delivery_hours: updatedDeliveryHours,
            });
            setHasBulkSelect(false);
            setDaySelected([]);
          }}
        />
      )}
    </Stack>
  );
};

export default RegularSchedule;
